import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { productInformationMsg, t } from '../../../common/i18n';
import {
  findBasketItemForId,
  getOfferForBasketItem,
  getOnlineModelForBasketItem,
} from '../../ShoppingBasket/shoppingBasketUtils';
import { usePhoneNumbers } from '../hooks/usePhoneNumbers';
import { anyRequired, handleAllContactsValidate } from '../utils/contactOrPurposeUtils';
import { StepButtons } from './common/StepButtons';
import { OrderItemUnits } from './products/OrderItemUnits';
import { ProductEntry } from './products/ProductEntry';
import type { PlaceOrderProps } from '../PlaceOrder';
import type { ContactsUpdateMethods, PlaceOrderFormData } from '../placeOrderTypes';
import type { ContactOrPurposeHandle } from './products/PlaceOrderContactOrPurpose/PlaceOrderContactOrPurpose';
import type { SubscriptionConfigurationHandle } from './products/SubscriptionConfigurationHandler';
import type { YttAdditionalFieldsHandle } from './products/YttAdditionalFields';
import type { JSX } from 'react';

import './ProductsStep.scss';

export interface ProductsStepProps extends PlaceOrderProps {
  onNext: () => void;
  onPrevious: () => void;
  orderSummary: JSX.Element;
  contactsUpdateMethods: ContactsUpdateMethods;
}

export const ProductsStep = (props: ProductsStepProps) => {
  const {
    shoppingBasket,
    onlineModels,
    onNext,
    onPrevious,
    orderSummary,
    mdmId,
    expiringEppSubscriptions,
    contactsUpdateMethods,
  } = props;
  const methods = useFormContext<PlaceOrderFormData>();
  const contactFormRefs = useRef<Record<string, ContactOrPurposeHandle | null>>({});
  const subscriptionConfigRefs = useRef<Record<string, SubscriptionConfigurationHandle | null>>({});
  const yttFieldRefs = useRef<Record<string, YttAdditionalFieldsHandle | null>>({});

  // Phone numbers management for voice subscriptions
  const { phoneNumbers, phoneNumbersLoading, onLoadMoreNumbers } = usePhoneNumbers({
    shoppingBasket,
    onlineModels,
    mdmId,
  });

  const handleNext = async () => {
    // Run both validations so all errors are displayed at once, not sequentially
    let contactValidateSuccess = true;
    if (anyRequired(contactFormRefs)) {
      contactValidateSuccess = await handleAllContactsValidate(contactFormRefs);
    }

    // Validate subscription configuration forms
    const subscriptionConfigValidations = await Promise.all(
      Object.values(subscriptionConfigRefs.current)
        .filter((ref): ref is SubscriptionConfigurationHandle => ref !== null)
        .map(ref => ref.validate())
    );
    const allSubscriptionConfigsValid = subscriptionConfigValidations.every(valid => valid);

    // Validate YTT additional fields
    const yttFieldValidations = Object.values(yttFieldRefs.current)
      .filter((ref): ref is YttAdditionalFieldsHandle => ref !== null)
      .map(ref => ref.validate());
    const allYttFieldsValid = yttFieldValidations.every(valid => valid);

    if (!contactValidateSuccess) {
      return;
    }

    if (!allSubscriptionConfigsValid) {
      // Find first ref with errors and scroll to it
      const firstErrorRef = Object.values(subscriptionConfigRefs.current).find(ref => ref && ref.getErrorCount() > 0);
      firstErrorRef?.scrollToFirstError();
      return;
    }

    if (!allYttFieldsValid) {
      const firstErrorRef = Object.values(yttFieldRefs.current).find(ref => ref && ref.getErrorCount() > 0);
      firstErrorRef?.scrollToFirstError();
      return;
    }

    onNext();
  };

  const watchedOrderItemUnits = methods.watch('products.placeOrderItemUnits') || [];

  const groupedByBasketId = watchedOrderItemUnits.reduce<Record<string, typeof watchedOrderItemUnits>>((acc, item) => {
    if (!acc[item.basketItemId]) {
      acc[item.basketItemId] = [];
    }
    acc[item.basketItemId].push(item);
    return acc;
  }, {});

  const orderItemUnits = Object.entries(groupedByBasketId).flatMap(([basketItemId, items], index) => {
    const basketItem = findBasketItemForId(shoppingBasket.items, basketItemId);
    if (!basketItem) {
      return null;
    }

    const onlineModelForItem = getOnlineModelForBasketItem(onlineModels, basketItem);
    const offer = getOfferForBasketItem(onlineModels, basketItem);
    if (!offer || !onlineModelForItem) {
      return null;
    }

    return (
      <li key={basketItem.id}>
        <div className="of-products-step--item-line">
          <div className="of-products-step--badge-container">
            <CL.Badge type="numeric" color="info" text={String(index + 1)} />
          </div>
          <ProductEntry
            basketItem={basketItem}
            offer={offer}
            onlineModel={onlineModelForItem}
            changeQuantity={props.shoppingBasketUpdateMethods.changeQuantity}
            campaignData={props.campaignData}
            allModels={onlineModels}
          />
          <OrderItemUnits
            {...props}
            contactsUpdateMethods={contactsUpdateMethods}
            orderItemUnits={items}
            basketItem={basketItem}
            onlineModel={onlineModelForItem}
            mdmId={mdmId}
            offer={offer}
            contactFormRefs={contactFormRefs}
            expiringEppSubscriptions={expiringEppSubscriptions}
            subscriptionConfigRefs={subscriptionConfigRefs}
            yttFieldRefs={yttFieldRefs}
            phoneNumbers={phoneNumbers}
            phoneNumbersLoading={phoneNumbersLoading}
            onLoadMoreNumbers={onLoadMoreNumbers}
            isFirstBasketItem={index === 0}
          />
        </div>
      </li>
    );
  });

  return (
    <div className={classNames('products-step')}>
      <h1>{t.R4EV(productInformationMsg)}</h1>
      <ul className="ds-shoppingcart-wrapper">{orderItemUnits}</ul>
      {orderSummary}
      <StepButtons onNext={handleNext} onPrevious={onPrevious} disableForward={phoneNumbersLoading} />
    </div>
  );
};
